import React, { useState, useEffect, useRef } from 'react';
import { Sun, Moon, LogIn, UserPlus, User as UserIcon, LogOut, Shield, Bell, Check, Trash2, X, MessageCircle, FileText, Tag, Settings } from 'lucide-react';
import { User, UserRole, AppNotification } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../lib/db';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
const ChatModal = React.lazy(() => import('./ChatModal'));
import Logo from './ui/Logo';
import { useToast } from '../contexts/ToastContext';

interface HeaderProps {
  currentUser: User | null;
  onLogout: () => void;
  onOpenAuth: (type: 'LOGIN' | 'REGISTER') => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const Header = ({ 
  currentUser, 
  onLogout, 
  onOpenAuth, 
  theme, 
  toggleTheme 
}: HeaderProps) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const { language, setLanguage, t } = useLanguage();
  const notificationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initial Fetch & Realtime Subscription
  useEffect(() => {
    if (!currentUser) return;
    
    // 1. İlk Yükleme
    const fetchData = async () => {
      try {
        const notifData = await api.notifications.getAll(currentUser.id);
        setNotifications(notifData);
        const convs = await api.messages.getConversations(currentUser.id);
        const msgCount = convs.reduce((acc, curr) => acc + curr.unreadCount, 0);
        setUnreadMessages(msgCount);
      } catch (e) { console.error(e); }
    };

    fetchData();

    // 2. Supabase Realtime: Bildirimler
    if (supabase) {
        const client = supabase; // TypeScript null check fix için local variable
        
        const notifChannel = client
            .channel('notifications_header')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'Notification',
                    filter: `userId=eq.${currentUser.id}`,
                },
                (payload: any) => {
                    const newNotif = payload.new as AppNotification;
                    setNotifications(prev => [newNotif, ...prev]);
                    addToast(newNotif.title, 'info'); // Toast ile de göster
                }
            )
            .subscribe();

        // 2. Supabase Realtime: Mesajlar (Okunmamış sayısını artırmak için)
        const msgChannel = client
            .channel('messages_header')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'Message',
                    filter: `receiverId=eq.${currentUser.id}`,
                },
                (payload: any) => {
                    // Eğer sohbet açık değilse sayacı artır
                    if (!showChat) {
                        setUnreadMessages(prev => prev + 1);
                        addToast('Yeni bir mesajınız var', 'info');
                    }
                }
            )
            .subscribe();

        return () => {
            client.removeChannel(notifChannel);
            client.removeChannel(msgChannel);
        };
    } else {
        // Fallback: Polling
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }
  }, [currentUser, showChat]);

  const unreadNotifCount = notifications.filter(n => !n.isRead).length;

  const handleMarkAsRead = async (id: string) => {
    await api.notifications.markAsRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
  };

  const handleMarkAllRead = async () => {
    if (!currentUser) return;
    await api.notifications.markAllAsRead(currentUser.id);
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleDeleteNotif = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await api.notifications.delete(id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getDisplayInfo = () => {
    if (!currentUser) return { name: '', initial: '', identifier: '' };
    // Safety check: ensure user properties exist
    const name = currentUser.companyName || currentUser.fullName || (currentUser.email ? currentUser.email.split('@')[0] : '') || currentUser.phone || 'Kullanıcı';
    const initialRaw = currentUser.companyName || currentUser.fullName || currentUser.email || 'U';
    const initial = initialRaw.charAt(0).toUpperCase();
    const identifier = currentUser.email || currentUser.phone || '';
    return { name, initial, identifier };
  };

  const { name, initial, identifier } = getDisplayInfo();

  return (
    <>
    <header className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-200 dark:border-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex items-center cursor-pointer group shrink-0" onClick={() => navigate('/')}>
            <Logo className="h-10 sm:h-14 w-auto transition-transform duration-300 group-hover:scale-105" />
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 sm:p-1 h-7 sm:h-9">
              <button onClick={() => setLanguage('tr')} className={`px-1.5 sm:px-3 h-full rounded text-[10px] sm:text-xs font-bold transition-all flex items-center justify-center ${language === 'tr' ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>TR</button>
              <button onClick={() => setLanguage('en')} className={`px-1.5 sm:px-3 h-full rounded text-[10px] sm:text-xs font-bold transition-all flex items-center justify-center ${language === 'en' ? 'bg-white dark:bg-gray-700 text-teal-600 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>EN</button>
            </div>

            <button onClick={toggleTheme} className="p-1.5 sm:p-2 rounded-full text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
              {theme === 'light' ? <Moon className="w-4 h-4 sm:w-5 sm:h-5" /> : <Sun className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>

            {currentUser ? (
              <div className="flex items-center gap-2 sm:gap-3">
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowChat(true); }} className="p-2 sm:p-2.5 rounded-full text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all relative">
                  <MessageCircle className="w-5 h-5" />
                  {unreadMessages > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-indigo-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse"></span>}
                </button>

                <div className="relative" ref={notificationRef}>
                  <button onClick={() => setShowNotifications(!showNotifications)} className="p-1.5 sm:p-2.5 rounded-full text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all relative">
                    <Bell className="w-5 h-5" />
                    {unreadNotifCount > 0 && <span className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse"></span>}
                  </button>
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-72 sm:w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 py-2 animate-in fade-in slide-in-from-top-5 z-[60] overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                        <h3 className="font-bold text-gray-900 dark:text-white text-sm">Bildirimler</h3>
                        {unreadNotifCount > 0 && <button onClick={handleMarkAllRead} className="text-xs text-indigo-600 dark:text-indigo-400 font-bold hover:underline flex items-center"><Check className="w-3 h-3 mr-1"/> Tümünü Oku</button>}
                      </div>
                      <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                        {notifications.length === 0 ? <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">Bildirim yok.</div> : 
                          notifications.map((notif) => (
                            <div key={notif.id} onClick={() => handleMarkAsRead(notif.id)} className={`px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors relative group ${!notif.isRead ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                               <h4 className={`text-sm ${!notif.isRead ? 'font-bold text-indigo-900 dark:text-indigo-200' : 'font-semibold text-gray-700 dark:text-gray-300'}`}>{notif.title}</h4>
                               <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 pr-6">{notif.message}</p>
                               {!notif.isRead && <span className="absolute top-4 right-4 w-2 h-2 bg-indigo-500 rounded-full"></span>}
                               <button onClick={(e) => handleDeleteNotif(notif.id, e)} className="absolute bottom-2 right-2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3"/></button>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <button onClick={() => setShowDropdown(!showDropdown)} className="flex items-center space-x-2 pl-1 pr-1 sm:pr-4 py-1 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-transparent focus:border-indigo-500">
                    <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-tr from-teal-500 to-blue-500 flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-md overflow-hidden shrink-0">
                      {currentUser.logoUrl ? <img src={currentUser.logoUrl} className="w-full h-full object-cover" /> : initial}
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-200 max-w-[100px] truncate">{name}</span>
                  </button>

                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 py-2 animate-in fade-in slide-in-from-top-5 z-50">
                      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('logged_in')}</p>
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{identifier}</p>
                      </div>
                      
                      {currentUser.role === UserRole.PROVIDER && <button onClick={() => { navigate('/panel/kurumsal'); setShowDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400 flex items-center"><UserIcon className="w-4 h-4 mr-2" /> {t('my_account')}</button>}
                      {currentUser.role === UserRole.ADMIN && <button onClick={() => { navigate('/panel/yonetici'); setShowDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400 flex items-center"><Shield className="w-4 h-4 mr-2" /> {t('admin_panel')}</button>}
                      
                      {/* Customer Menu Items Updated */}
                      {currentUser.role === UserRole.CUSTOMER && (
                        <>
                          <button onClick={() => { navigate('/panel/musteri/talepler'); setShowDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400 flex items-center">
                            <FileText className="w-4 h-4 mr-2" /> {t('my_requests')}
                          </button>
                          <button onClick={() => { navigate('/panel/musteri/teklifler'); setShowDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400 flex items-center">
                            <Tag className="w-4 h-4 mr-2" /> Gelen Teklifler
                          </button>
                          <button onClick={() => { navigate('/panel/musteri/ayarlar'); setShowDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400 flex items-center">
                            <Settings className="w-4 h-4 mr-2" /> Ayarlar
                          </button>
                        </>
                      )}

                      <button onClick={() => { onLogout(); setShowDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"><LogOut className="w-4 h-4 mr-2" /> {t('logout')}</button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-2">
                 <button onClick={() => onOpenAuth('LOGIN')} className="px-2 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-sm font-bold text-gray-700 dark:text-gray-200 hover:text-teal-600 dark:hover:text-teal-400 transition-colors flex items-center shrink-0 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"><LogIn className="w-3.5 h-3.5 sm:mr-2" /><span className="hidden sm:inline">{t('member_login')}</span></button>
                <button onClick={() => onOpenAuth('REGISTER')} className="px-2 sm:px-5 py-1.5 sm:py-2.5 text-[10px] sm:text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-500 rounded-lg sm:rounded-xl shadow-lg shadow-teal-200 dark:shadow-none transition-all flex items-center shrink-0"><UserPlus className="w-3.5 h-3.5 sm:mr-2" /><span className="hidden sm:inline">{t('corp_register')}</span></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
    {showChat && currentUser && (
      <React.Suspense fallback={null}>
        <ChatModal currentUser={currentUser} onClose={() => setShowChat(false)} />
      </React.Suspense>
    )}
    </>
  );
};

export default Header;